type NumericValues = ArrayLike<number> | null | undefined;

// Math.min/max with the spread operator overflows the call stack past ~100k items,
// so these iterate instead. The loops are written out rather than sharing a callback:
// a shared comparator makes the hot call site polymorphic and V8 stops inlining it.
//
// Only null needs an explicit guard. undefined and NaN fail every relational
// comparison, so they can never win a min or a max and are skipped implicitly.
// null coerces to 0 and would win, so it is tested — but only after the comparison,
// which most values fail, keeping the common path at a single check.

/** Minimum across one or more numeric arrays. Returns Infinity when there is no valid value. */
export const arrayMin = (...arrays: NumericValues[]): number => {
    let min = Number.POSITIVE_INFINITY;

    for (const values of arrays) {
        if (!values) continue;

        for (let i = 0, len = values.length; i < len; i++) {
            const value = values[i];
            if (value < min && value !== null) min = value;
        }
    }

    return min;
};

/** Maximum across one or more numeric arrays. Returns -Infinity when there is no valid value. */
export const arrayMax = (...arrays: NumericValues[]): number => {
    let max = Number.NEGATIVE_INFINITY;

    for (const values of arrays) {
        if (!values) continue;

        for (let i = 0, len = values.length; i < len; i++) {
            const value = values[i];
            if (value > max && value !== null) max = value;
        }
    }

    return max;
};

/** Both bounds in a single pass. Prefer this over calling arrayMin and arrayMax on the same data. */
export const arrayExtent = (...arrays: NumericValues[]): { min: number; max: number } => {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    for (const values of arrays) {
        if (!values) continue;

        for (let i = 0, len = values.length; i < len; i++) {
            const value = values[i];
            if (value < min && value !== null) min = value;
            if (value > max && value !== null) max = value;
        }
    }

    return { min, max };
};
